import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FormEvent, ReactElement } from 'react'
import { useState } from 'react'
import type { HouseholdEntity } from '~/api/client'
import { api } from '~/api/client'
import { Card, CardHead } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'

const CSS = `
.nw-entities ul {
  border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-md);
  list-style: none; margin: var(--nw-space-4) 0 var(--nw-space-3); padding: 0;
}
.nw-entities li { align-items: center; border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); display: flex; flex-wrap: wrap; gap: var(--nw-space-2) var(--nw-space-3); padding: var(--nw-space-2) var(--nw-space-3); }
.nw-entities li:last-child { border-bottom: 0; }
.nw-entities li[data-retired='true'] .nw-meta { color: var(--nw-muted); }
/* Wide enough for a company name and no wider: the field is a name, not the row. */
.nw-entities li form { flex: 0 1 340px; min-width: 12ch; }
.nw-entities li input[type='text'] { width: 100%; }
.nw-entities li .nw-meta { flex: 0 0 auto; white-space: nowrap; }
.nw-entities li .nw-btn { flex: 0 0 auto; margin-left: auto; }
.nw-entities .nw-meta { color: var(--nw-body-ink); font-size: var(--nw-text-chip); }
.nw-entities .nw-kind { color: var(--nw-muted); font-size: var(--nw-text-chip); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-entities form { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-2); }
.nw-entities .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); }
`

/**
 * The household's own people and companies, and the only place they can be
 * made.
 *
 * A household is born holding one placeholder person, and every question the
 * product asks about whose money something is is asked against this list. Until
 * it could be added to, the distinctions the rest of the product is built on —
 * what a company holds against what a person can spend, and the tax between
 * them — were unreachable for anyone who had not been seeded by hand.
 *
 * Shown to every household, including the one-person one that none of the cards
 * below it are offered to. It is the door those gates are reached through: the
 * ownership card wants a second entity and the tax card wants a company, and
 * both are made here. Withholding it because it is not needed yet would leave a
 * household with no way to arrive at needing it.
 */
export function EntitiesPanel(): ReactElement {
  const client = useQueryClient()
  const listing = useQuery({
    queryKey: ['entities', 'all'],
    queryFn: () => api.everyEntity(),
  })

  // A name and whether an entity is offered are read by pages with queries of
  // their own — the attribution menus, the holdings table, every figure filed
  // under a key — so what is refreshed is everything rather than this list.
  const settled = { onSuccess: () => client.invalidateQueries() }

  const create = useMutation({
    mutationFn: (input: { name: string; kind: HouseholdEntity['kind'] }) => api.createEntity(input),
    ...settled,
  })
  const rename = useMutation({
    mutationFn: (input: { key: string; name: string }) => api.renameEntity(input.key, input.name),
    ...settled,
  })
  const retire = useMutation({ mutationFn: (key: string) => api.retireEntity(key), ...settled })
  const restore = useMutation({ mutationFn: (key: string) => api.unretireEntity(key), ...settled })

  const entities = listing.data?.entities ?? []
  const busy = create.isPending || rename.isPending || retire.isPending || restore.isPending

  return (
    <Card className="nw-entities">
      <style>{CSS}</style>
      <CardHead
        id="nw-entities-heading"
        title="People and companies"
        lede="Every account, address and hand-entered holding is filed under one of these. A company holds money a person cannot spend until it has been taken out and taxed, which is the distinction the headline figures are built on — so a household with a company has to be able to say so."
      />

      <Refusal error={listing.error ?? failureOf(create, rename, retire, restore)} />

      <ul>
        {entities.map((entity) => (
          <EntityRow
            key={entity.key}
            entity={entity}
            disabled={busy}
            onRename={(name) => rename.mutate({ key: entity.key, name })}
            onRetire={() => retire.mutate(entity.key)}
            onRestore={() => restore.mutate(entity.key)}
          />
        ))}
      </ul>

      <AddEntity disabled={busy || listing.isPending} onAdd={(input) => create.mutate(input)} />
    </Card>
  )
}

function EntityRow({
  entity,
  disabled,
  onRename,
  onRetire,
  onRestore,
}: {
  entity: HouseholdEntity
  disabled: boolean
  onRename: (name: string) => void
  onRetire: () => void
  onRestore: () => void
}): ReactElement {
  const [draft, setDraft] = useState<string | null>(null)
  const retired = entity.retiredOn !== undefined
  const shown = draft ?? entity.name

  const commit = (event: FormEvent): void => {
    event.preventDefault()
    const name = shown.trim()
    setDraft(null)
    if (name !== '' && name !== entity.name) onRename(name)
  }

  return (
    <li data-retired={String(retired)}>
      <form onSubmit={commit}>
        <input
          type="text"
          value={shown}
          disabled={disabled}
          aria-label={`Name of ${entity.name}`}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
        />
      </form>
      <span className="nw-meta">
        {entity.kind}
        {retired ? ` · retired ${entity.retiredOn ?? ''}` : ''}
      </span>
      {retired ? (
        <button type="button" disabled={disabled} onClick={onRestore}>
          Offer again
        </button>
      ) : (
        <button type="button" disabled={disabled} onClick={onRetire}>
          Retire
        </button>
      )}
    </li>
  )
}

function AddEntity({
  disabled,
  onAdd,
}: {
  disabled: boolean
  onAdd: (input: { name: string; kind: HouseholdEntity['kind'] }) => void
}): ReactElement {
  const [name, setName] = useState('')
  // A household is born as one person, so the next thing it names is far more
  // often another person than a company — and offering Company first told a
  // one-person household that a company was the expected shape.
  const [kind, setKind] = useState<HouseholdEntity['kind']>('person')

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const trimmed = name.trim()
    if (trimmed === '') return
    setName('')
    onAdd({ name: trimmed, kind })
  }

  return (
    <form onSubmit={submit}>
      <label htmlFor="nw-entity-name" className="nw-kind">
        Add
      </label>
      <input
        id="nw-entity-name"
        type="text"
        value={name}
        disabled={disabled}
        placeholder="Name"
        onChange={(event) => setName(event.target.value)}
      />
      <select
        value={kind}
        disabled={disabled}
        aria-label="What it is"
        onChange={(event) => setKind(event.target.value as HouseholdEntity['kind'])}
      >
        <option value="person">Person</option>
        <option value="company">Company</option>
      </select>
      <button type="submit" disabled={disabled || name.trim() === ''}>
        Add
      </button>
    </form>
  )
}

/**
 * `last_entity` is the server refusing rather than failing, and a household
 * reading the wire's word for it would learn nothing about why.
 */
function Refusal({ error }: { error: Error | null }): ReactElement | null {
  if (error === null) return null
  const message =
    error.message === 'last_entity'
      ? 'A household keeps at least one entity: everything it holds is attributed to one.'
      : error.message
  return <p className="nw-error">{message}</p>
}

function failureOf(...mutations: readonly { error: Error | null }[]): Error | null {
  return mutations.map((one) => one.error).find((one) => one !== null) ?? null
}
